import { createRouter, createWebHashHistory, type RouteLocationNormalized } from 'vue-router';
import { ElMessage } from 'element-plus';
import { hasPerm, session } from '@/core';
import { routes } from './routes';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { usePermissionStore, wirePermissionContext } from '@/stores/permission';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    crumb?: string;
    /** 菜单级权限码 */
    perm?: string;
    menu?: boolean;
    menuOrder?: number;
    /** 左侧导航分组名（未设＝平铺）；既有「BPO 机构管理」＋本批新增「交付机构管理」（契约 §6.1） */
    menuGroup?: string;
    activeMenu?: string;
    /**
     * 成员屏共用一个组件，靠这条 meta 区分要列哪一族成员。
     * 值域是两腿的并集：交付腿 `AGENCY_ADMIN`/`COURIER`（契约 §6.1 两枚路由 + §6.3 前端过滤），
     * t9 起加 BPO 腿 `ADMIN`/`AGENT`（用户 m00149：录单员管理／BPO管理员管理两枚平台级入口）。
     */
    memberRole?: 'COURIER' | 'AGENCY_ADMIN' | 'ADMIN' | 'AGENT';
    /** t9：平台级人员列表读哪一条腿（bpo → /bpo-agencies/members，delivery → /delivery-agencies/members） */
    agency?: 'bpo' | 'delivery';
    public?: boolean;
  }
}

/** 管理端与交付后台统一约定：hash 路由（便于静态托管与端隔离部署，无需服务端 rewrite） */
export const router = createRouter({
  history: createWebHashHistory(import.meta.env.VITE_PUBLIC_PATH || '/'),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

wirePermissionContext();

function isPublic(route: RouteLocationNormalized): boolean {
  return route.matched.some((record) => record.meta.public === true);
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const app = useAppStore();
  const permission = usePermissionStore();

  if (isPublic(to)) {
    if (to.name === 'login' && auth.isAuthenticated) return { path: '/dashboard' };
    return true;
  }

  if (!session.getAccessToken()) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  if (!auth.user) auth.restore();
  if (!auth.user) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  // 会话绝对截止（后端 expiresAt：24h 上限、自登录起算不滑动续期）⇒ 强制登出
  if (auth.expiresAt && auth.expiresAt < Date.now()) {
    ElMessage.warning('会话已超时，请重新登录');
    auth.forceLogout();
    return { path: '/login', query: { redirect: to.fullPath, reason: 'timeout' } };
  }

  // §6.2-7：刷新后先按 profile 重建权限态再判权限；端点不可用时沿用登录时服务端签发的 interim 快照（state=stale）
  if (auth.snapshotState === 'pending') await auth.syncProfile();

  // 跨端登录隔离（用户 m02355）：交付机构账号只属于交付管理后台（5196）。登录层已按 X-Client-End 拒绝，
  // 这里再对「旧令牌/绕过登录口」的会话做启动自检，清令牌与快照后回登录页。
  if (auth.user?.console === 'DELIVERY_ADMIN') {
    void auth.logout(false);
    return { path: '/login', query: { redirect: to.fullPath, reason: 'agency-side' } };
  }

  // 端绑定互斥：外部角色（BPO客服/交付专员）禁止进入管理端
  if (!permission.endAllowed) {
    return { path: '/forbidden', query: { reason: 'end-binding' } };
  }

  // 菜单级权限
  if (to.meta.perm && !hasPerm(to.meta.perm)) {
    return { path: '/forbidden', query: { reason: 'perm', perm: to.meta.perm } };
  }

  app.setCrumb(to.meta.crumb || to.meta.title || '');
  return true;
});

router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} · 营销交付中控系统` : '营销交付中控系统';
  if (document.title !== title) document.title = title;
});
